'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import { trackEvent } from '@/components/GoogleAnalytics';

const OFFER_END_TIMESTAMP = new Date('2026-03-19T23:59:59+05:30').getTime();
const COUPON_CODE = 'PADWA2026';
const IMAGE_CDN_BASE = process.env.NEXT_PUBLIC_IMAGE_CDN_URL || '';

// Exported separately so you can easily update FAQs for this page
export const gudiPadwaFaqs = [
  {
    question: 'Is the ₹1000/- price really for a full year?',
    answer:
      'Yes! As part of our Gudi Padwa Shubh Aarambh offer, you get complete Premium access for 12 months at just ₹1000/- when you use the code PADWA2026. No hidden monthly charges.',
  },
  {
    question: 'Why am I seeing ₹2000/- on the payment page?',
    answer:
      'Our standard discounted price is ₹2000/-. To get the special festive price of ₹1000/-, simply apply the coupon code PADWA2026 at the checkout. The price will instantly drop by 50%!',
  },
  {
    question: 'Can I renew my existing membership with this offer?',
    answer:
      'Absolutely! If you are an existing user, applying this code will add 12 months to your current validity at this special rate. It’s the best time to extend your search.',
  },
  {
    question: 'What extra benefits do I get in the Premium Plan?',
    answer:
      'Premium members get 3x more responses. You can view verified contact numbers, initiate unlimited chats, see who viewed your profile, and get a "Premium Badge" that builds trust with potential matches.',
  },
  {
    question: 'Is my payment secure?',
    answer:
      '100%. We use industry-standard encrypted payment gateways. You can pay via UPI (GPay, PhonePe), Credit/Debit cards, or Net Banking securely.',
  },
  {
    question: 'How long is the PADWA2026 code valid?',
    answer:
      'This is a limited-time festive flash sale. The code is valid only until the Gudi Padwa celebrations end. We recommend upgrading before the Gudi is lowered to lock in this "once-a-year" price.',
  },
  {
    question: 'Which Marathi community Vadhu Var profiles could be found here?',
    answer:
      'Marathi Vadhu Var Suchak is for all Marathi communities. Below are a few examples of Marathi communities:',
    communities: [
      'Maratha Vadhu Var',
      '96 Kuli Maratha Vadhu Var (Including 96 Kuli Maratha surname list)',
      'Deokar Maratha Vadhu Var',
      'Kokanastha Maratha Vadhu Var - For e.g. Tawde Vivah',
      'Brahmin Vadhu Var',
      'Mali Vadhu Var',
      'Dhangar Vadhu Var',
      'Vanjari Vadhu Var',
      'Veershaiv Vadhu Var',
      'Dhor Kakkayya Caste Vadhu Var',
      'Kunbi Caste Vadhu Var',
      'Matang Vadhu Var',
      'Leva Patil Vadhu Var',
      'Lingayat Caste Vadhu Var',
      'Bhandari Vadhu Var',
      'Buddhist Vadhu Var',
    ],
  },
];

export default function GudiPadwaMarch2026Client() {
  const [remainingSeconds, setRemainingSeconds] = useState(() => {
    if (typeof window === 'undefined') return 0;
    const now = Date.now();
    const diffMs = OFFER_END_TIMESTAMP - now;
    return diffMs > 0 ? Math.floor(diffMs / 1000) : 0;
  });
  const [copied, setCopied] = useState(false);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setRemainingSeconds(() => {
        const now = Date.now();
        const diffMs = OFFER_END_TIMESTAMP - now;
        if (diffMs <= 0) {
          clearInterval(interval);
          return 0;
        }
        return Math.floor(diffMs / 1000);
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const hours = Math.floor(remainingSeconds / 3600);
  const minutes = Math.floor((remainingSeconds % 3600) / 60);
  const seconds = remainingSeconds % 60;

  const handleCopyCoupon = async () => {
    try {
      await navigator.clipboard.writeText(COUPON_CODE);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore clipboard errors
    }
  };

  return (
    <div className="bg-white">
      <Header variant="transparent" />

      {/* Hero Section */}
      <section
        className="relative flex items-end min-h-screen overflow-hidden"
        style={{
          backgroundImage: `url(${IMAGE_CDN_BASE}/assets/img/gudi-padwa-2026-banner.jpg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="container mx-auto px-4 relative z-10 mb-28">
          <div className="max-w-3xl text-left text-white">
            <h1
              className="text-[2.1rem] sm:text-[3rem] font-bold leading-tight mb-4"
              style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.2)' }}
            >
              या पाडव्याला, रेशीमगाठी जुळवा आनंदाने!
            </h1>
            <p className="text-[1.1rem] sm:text-2xl mb-8">
              Get 12 Months Premium at the lowest price ever
            </p>
            <Link
              href="/membership"
              className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-full font-medium text-lg tracking-wide transition-all duration-300 transform hover:-translate-y-1 shadow-lg inline-block max-[599px]:w-auto border border-white"
              onClick={() => trackEvent('click_claim_50_off', 'gudi_padwa_hero', 'Claim 50% Off')}
            >
              Claim 50% Off Now
            </Link>
          </div>
        </div>
      </section>

      {/* Brides / Grooms Search Section (from homepage) */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              तुमच्या आयुष्याचा जोडीदार फक्त एका क्लीकवर!
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
              Marathi वधू वर शोधण्यासाठी वधू किंवा वर यांपैकी निवडा आणि प्रोफाईल्स पहा.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                href="/brides"
                className="px-5 py-2 rounded-full font-normal text-base border-2 border-primary-600 text-primary-600 hover:border-primary-700 hover:text-primary-700 transition-colors"
              >
                Brides ( वधू )
              </Link>
              <Link
                href="/grooms"
                className="px-5 py-2 rounded-full font-normal text-base border-2 border-primary-600 text-primary-600 hover:border-primary-700 hover:text-primary-700 transition-colors"
              >
                Grooms ( वर )
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Double Discount Offer Section */}
      <section className="py-20 bg-gradient-to-b from-amber-50 via-white to-amber-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Gudi Padwa Double Discount Offer
            </h2>
            <p className="text-lg text-gray-700">
              Celebrate Gudi Padwa with a special double-discount on your 12 Months Premium membership.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-50 border border-primary-200 shadow-sm">
              <span className="inline-flex h-2 w-2 rounded-full bg-primary-500 animate-pulse" />
              <p className="text-xs sm:text-sm font-semibold text-primary-800">
                Only <span className="font-extrabold">300</span> members get this special ₹1000 price. Don&apos;t miss out.
              </p>
            </div>
          </div>

          <div className="max-w-4xl mx-auto grid gap-8 md:grid-cols-[2fr,1.5fr] items-stretch">
            {/* Price Ladder */}
            <div className="bg-white rounded-2xl shadow-xl border border-primary-100 p-6 sm:p-8 text-left">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Limited Time Gudi Padwa Price Ladder
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm uppercase tracking-wide text-gray-500">Original Value</p>
                    <p className="text-lg font-semibold text-gray-400 line-through">₹3500</p>
                  </div>
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">
                    Regular Rate
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm uppercase tracking-wide text-gray-500">Standard Price</p>
                    <p className="text-lg font-semibold text-gray-400 line-through">₹2000</p>
                  </div>
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-secondary-50 text-secondary-600 text-xs font-semibold">
                    Everyday Premium
                  </span>
                </div>

                <div className="relative mt-4 pt-4 border-t border-dashed border-primary-200">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 rounded-full bg-primary-500 text-white text-xs font-semibold shadow-md">
                      Gudi Padwa Special
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mt-4">
                    <div className="w-full">
                      <p className="text-sm uppercase tracking-wide text-primary-700">
                        Gudi Padwa Special Price
                      </p>
                      <p className="text-3xl sm:text-4xl font-extrabold text-primary-600 mt-1">
                        ₹1000
                      </p>
                      <p className="text-sm text-primary-800 mt-1 font-medium">
                        Flat 50% Off on 12 Months Premium
                      </p>
                    </div>
                    <Link
                      href="/membership"
                      className="inline-flex items-center justify-center px-4 sm:px-6 py-3 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm sm:text-base font-semibold shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 w-full sm:w-auto whitespace-nowrap shrink-0"
                      onClick={() => trackEvent('click_upgrade_now', 'gudi_padwa_offer_ladder', 'Upgrade Now')}
                    >
                      Upgrade Now
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Coupon + Timer */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl shadow-lg border border-primary-100 p-6">
                <p className="text-sm font-medium text-gray-700 mb-2">Apply this Gudi Padwa Coupon</p>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <div className="flex-1">
                    <div className="w-full px-4 py-3 rounded-xl border-2 border-dashed border-primary-500 bg-primary-50 text-primary-800 font-mono text-lg tracking-[0.25em] text-center select-all">
                      {COUPON_CODE}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleCopyCoupon}
                    className="px-4 py-3 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-semibold text-sm shadow-md transition-colors"
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <div className="mt-3 space-y-1">
                  <p className="text-xs text-gray-500 text-left">
                    Use this coupon during payment to unlock the Gudi Padwa 50% Off price.
                  </p>
                  <p className="text-xs font-semibold text-primary-800 text-left">
                    Hurry – this <span className="font-bold">₹3500 → ₹2000 → ₹1000</span> offer is limited to the first{' '}
                    <span className="font-extrabold">300</span> premium members only.
                  </p>
                </div>
              </div>

              <div className="bg-secondary-900 rounded-2xl shadow-lg p-6 text-center text-white">
                <p className="text-sm uppercase tracking-[0.25em] text-secondary-200 mb-3">
                  Limited Time Gudi Padwa Offer
                </p>
                <p className="text-lg font-semibold mb-2">
                  Offer ends in
                </p>
                <div className="flex justify-center gap-3 mb-3">
                  <div className="flex flex-col items-center">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-secondary-800 flex items-center justify-center text-2xl sm:text-3xl font-bold">
                      {hours.toString().padStart(2, '0')}
                    </div>
                    <span className="text-xs mt-1 text-gray-300">Hours</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-secondary-800 flex items-center justify-center text-2xl sm:text-3xl font-bold">
                      {minutes.toString().padStart(2, '0')}
                    </div>
                    <span className="text-xs mt-1 text-gray-300">Minutes</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-secondary-800 flex items-center justify-center text-2xl sm:text-3xl font-bold">
                      {seconds.toString().padStart(2, '0')}
                    </div>
                    <span className="text-xs mt-1 text-gray-300">Seconds</span>
                  </div>
                </div>
                <p className="text-xs text-gray-300">
                  Lock in your 12 Months Premium at just ₹1000 before the countdown hits zero.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Membership Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Premium Membership Benefits
            </h2>
            <p className="text-lg text-gray-600">
              Your Gudi Padwa 12 Months Premium unlocks generous usage limits and all powerful Marathi matrimony features.
            </p>
          </div>

          <div className="max-w-4xl mx-auto bg-gradient-to-br from-primary-50 via-white to-amber-50 border border-amber-100 rounded-2xl shadow-xl overflow-hidden">
            <div className="px-6 sm:px-8 py-4 border-b border-amber-100 flex items-center justify-between">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                Usage Limits with Gudi Padwa 12 Months Premium
              </h3>
              <span className="hidden sm:inline-flex items-center px-3 py-1 rounded-full bg-amber-500 text-white text-xs font-semibold">
                Best Value for 1 Year
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-xs sm:text-sm">
                <thead>
                  <tr className="bg-amber-50/70">
                    <th className="px-4 sm:px-6 py-3 font-semibold text-gray-700 w-1/2">
                      Premium Benefit
                    </th>
                    <th className="px-4 sm:px-6 py-3 font-semibold text-gray-700 text-right whitespace-nowrap w-1/2">
                      12 Months Gudi Padwa Offer
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-amber-100 hover:bg-amber-50/40">
                    <td className="px-4 sm:px-6 py-3 text-gray-800">
                      Mobile Number Access Count
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-right">
                      <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                        Up to 150 contacts
                      </span>
                    </td>
                  </tr>
                  <tr className="border-t border-amber-100 hover:bg-amber-50/40">
                    <td className="px-4 sm:px-6 py-3 text-gray-800">
                      Send Message Count
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-right">
                      <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                        Up to 150 messages
                      </span>
                    </td>
                  </tr>
                  <tr className="border-t border-amber-100 hover:bg-amber-50/40">
                    <td className="px-4 sm:px-6 py-3 text-gray-800">
                      Chat Users Count
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-right">
                      <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                        Chat with 150 matches
                      </span>
                    </td>
                  </tr>
                  <tr className="border-t border-amber-100 hover:bg-amber-50/40">
                    <td className="px-4 sm:px-6 py-3 text-gray-800">
                      View Horoscope &amp; Kundali Matching
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-right">
                      <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold">
                        Up to 250 matches
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="px-6 sm:px-8 py-5 border-t border-amber-100 bg-white/70">
              <p className="text-sm font-semibold text-gray-900 mb-2">
                Plus all powerful Premium features:
              </p>
              <div className="grid gap-2 sm:grid-cols-2 text-sm text-gray-700">
                <ul className="space-y-1 list-disc list-inside">
                  <li>Send Interest and shortlist favourite profiles</li>
                  <li>Full profile view with photos &amp; details</li>
                  <li>Smart search filters by community, city &amp; education</li>
                </ul>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Daily recommended matches in your inbox</li>
                  <li>Better visibility in search results</li>
                  <li>Priority support from Marathi Vadhu Var Suchak team</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories (copied from homepage) */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Success Stories
          </h2>
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-2xl shadow-lg">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-primary-600 font-semibold">A</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Anjali & Amit</h3>
                    <p className="text-sm text-gray-600">Married in 2023</p>
                  </div>
                </div>
                <p className="text-gray-600">
                  &ldquo;They helped us find each other. The cultural compatibility matching was perfect and our families are so happy!&rdquo;
                </p>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-lg">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-secondary-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-secondary-600 font-semibold">P</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Priya & Prashant</h3>
                    <p className="text-sm text-gray-600">Married in 2024</p>
                  </div>
                </div>
                <p className="text-gray-600">
                  &ldquo;We found our perfect match within 6 months. The personalized matching and family support made our journey smooth and meaningful.&rdquo;
                </p>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-lg">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-primary-600 font-semibold">S</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Sneha & Sameer</h3>
                    <p className="text-sm text-gray-600">Married in 2024</p>
                  </div>
                </div>
                <p className="text-gray-600">
                  &ldquo;The community-focused approach made all the difference. We connected with families who shared our values and traditions.&rdquo;
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section (copied from homepage) */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Frequently Asked Questions
          </h2>
          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              {gudiPadwaFaqs.map((faq, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                  <button
                    onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <h3 className="text-lg font-semibold text-gray-900">
                      {faq.question}
                    </h3>
                    <svg
                      className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${
                        openFAQ === index ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div
                    className={`px-6 transition-all duration-300 ease-in-out ${
                      openFAQ === index ? 'pb-4 max-h-96 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                    }`}
                  >
                    <div className="text-gray-600 leading-relaxed py-3">
                      <p className="mb-3">{faq.answer}</p>
                      {faq.communities && (
                        <ul className="list-disc list-inside space-y-1 ml-4">
                          {faq.communities.map((community: string, idx: number) => (
                            <li key={idx} className="text-gray-600">
                              {community}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

